import { skipToken } from "@reduxjs/toolkit/query";
import type { TestSignalProposalToolOutput } from "@repo/app-types/entities/configurationAgent";
import {
  copilotProposalIdSchema,
  type CopilotProposal,
} from "@repo/app-types/entities/copilotProposal";
import { defaultDateRange } from "@repo/app-types/entities/dateRange";
import type { SignalId, SignalPayload } from "@repo/app-types/entities/signal";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DateTime } from "luxon";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  useGetPendingSignalProposalQuery,
  useResolveProposalMutation,
} from "../../../api/entities/copilotProposal.js";
import {
  useCreateSignalMutation,
  useDeleteSignalMutation,
  useGetSignalQuery,
  useSaveSignalMutation,
  useTestSignalProposalMutation,
} from "../../../api/entities/signal.js";
import { appPaths } from "../../../app/appPaths.js";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard.js";
import { useReportEditorProposal } from "../../researchAgent/editorProposalsSlice.js";
import { SignalEditorContainer } from "./signalEditorContainer.js";
import { SIGNAL_PROPOSAL_QUERY_PARAM } from "./signalEditorUrlState.js";
import {
  cleanMetadataSchema,
  isSignalFormDirty,
  mergeProposalIntoForm,
  readFormFields,
  type SignalFormFields,
} from "./signalFormHelpers.js";
import {
  SignalTestConfigModal,
  type TestSignalFiltersUi,
} from "./signalTestConfigModal.js";
import { SignalTestMatchesModal } from "./signalTestMatchesModal.js";

type TestResultsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "complete";
      data: TestSignalProposalToolOutput;
      completedAt: DateTime;
    };

const EMPTY_FORM: SignalFormFields = {
  name: "",
  description: "",
  metadata_schema: { fields: [] },
  playbook_scope: { type: "all" },
};

/**
 * Signals use explicit saves: edits stay local until Save makes them the
 * signal's new live version.
 */
export const SignalEditorPage: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { signalId } = useParams<{ signalId: SignalId }>();
  const isNew = !signalId;
  const navigate = useNavigate();

  const [createdSignalId, setCreatedSignalId] = useState<SignalId | null>(null);
  const createdSignalIdRef = useRef<SignalId | null>(null);

  const {
    data: signal,
    isLoading: isLoadingSignal,
    isError,
  } = useGetSignalQuery(signalId ?? createdSignalId ?? skipToken);
  const [createSignal, { isLoading: isCreating }] = useCreateSignalMutation();
  const [saveSignal, { isLoading: isSavingSignal }] = useSaveSignalMutation();
  const [deleteSignal, { isLoading: isDeleting }] = useDeleteSignalMutation();
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();
  const [testSignalProposal] = useTestSignalProposalMutation();

  const [form, setForm] = useState<SignalFormFields>(EMPTY_FORM);
  // The proposal in the draft, if any. Save applies it; a new signal takes the
  // id a `create_signal` proposal minted, the one the chat already knows it by.
  const [linkedProposal, setLinkedProposal] =
    useState<CopilotProposal<"signal"> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // The form is seeded from the stored draft once; later cache updates are
  // the form's own saves.
  const [isFormInitialized, setIsFormInitialized] = useState(isNew);

  const [testResultsState, setTestResultsState] = useState<TestResultsState>({
    status: "idle",
  });
  // Within-session memory of the filters used on the last actual run. When
  // non-null, the main Test signal click re-runs with these directly; otherwise
  // it opens the config modal so the user can pick.
  const [lastRunFilters, setLastRunFilters] =
    useState<TestSignalFiltersUi | null>(null);
  const [isTestConfigOpen, setIsTestConfigOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);

  const updateForm = useCallback((fields: Partial<SignalFormFields>) => {
    setForm((current) => ({ ...current, ...fields }));
  }, []);

  useEffect(() => {
    if (signal && !isFormInitialized) {
      setForm(readFormFields(signal));
      setIsFormInitialized(true);
    }
  }, [signal, isFormInitialized]);

  // Until a test is explicitly configured, tests follow the form's current
  // playbook scope. Memoized because the config modal re-seeds its local state
  // whenever this object's identity changes.
  const effectiveTestFilters: TestSignalFiltersUi = useMemo(
    () =>
      lastRunFilters ?? {
        playbookScope: form.playbook_scope,
        teammateGroupIds: null,
        dateRange: defaultDateRange,
      },
    [lastRunFilters, form.playbook_scope],
  );

  // The form as the server would store it. Dirty state is compared with the
  // live version because the user has not committed an edit until Save.
  const draftPayload = useMemo<SignalPayload>(
    () => ({
      name: form.name.trim(),
      description: form.description.trim(),
      metadata_schema: cleanMetadataSchema(form.metadata_schema),
      playbook_scope: form.playbook_scope,
    }),
    [form],
  );
  const isFormValid =
    draftPayload.name !== "" && draftPayload.description !== "";
  // A signal that has never been saved live has nothing to be unchanged from.
  const savedSignal = signal?.live_version_id ? signal : undefined;
  const hasUnsavedChanges = isSignalFormDirty(
    savedSignal,
    draftPayload.name,
    draftPayload.description,
    draftPayload.metadata_schema ?? { fields: [] },
    draftPayload.playbook_scope,
  );

  const runTestWithFilters = useCallback(
    async (filters: TestSignalFiltersUi) => {
      if (!isFormValid) return;

      setTestResultsState({ status: "loading" });

      const now = DateTime.now();
      const rangeEnd = filters.dateRange.end ?? now;
      const rangeStart = rangeEnd.minus(filters.dateRange.length);

      try {
        const data = await testSignalProposal({
          ...(signalId ? { signalId } : {}),
          signal: {
            name: form.name,
            description: form.description,
            metadata_schema: cleanMetadataSchema(form.metadata_schema),
            playbook_scope: form.playbook_scope,
          },
          filters: {
            playbookScope: filters.playbookScope,
            ...(filters.teammateGroupIds
              ? { teammateGroupIds: filters.teammateGroupIds }
              : {}),
            lastRecordedAtRange: {
              after: rangeStart.startOf("day").toUTC().toISO(),
              before: rangeEnd.endOf("day").toUTC().toISO(),
            },
          },
        }).unwrap();

        setTestResultsState({
          status: "complete",
          data,
          completedAt: DateTime.now(),
        });
      } catch (err) {
        console.error("Failed to test signal:", err);
        setTestResultsState({ status: "error" });
      }
    },
    [signalId, form, isFormValid, testSignalProposal],
  );

  const handleRunTestFromModal = useCallback(
    (filters: TestSignalFiltersUi) => {
      setIsTestConfigOpen(false);
      setLastRunFilters(filters);
      runTestWithFilters(filters);
    },
    [runTestWithFilters],
  );

  // Open config modal if filters have not been configured yet
  const handleTestSignalClick = useCallback(() => {
    if (lastRunFilters) {
      runTestWithFilters(lastRunFilters);
    } else {
      setIsTestConfigOpen(true);
    }
  }, [lastRunFilters, runTestWithFilters]);

  const handleConfigureTestClick = useCallback(() => {
    setIsTestConfigOpen(true);
  }, []);

  // A suggestion is merged into the draft, keeping the user's other unsaved
  // edits, and its evidence becomes the test result. It stays pending until
  // Save writes the draft, which applies it.
  const putProposalIntoDraft = useEffectEvent(
    (linkedProposal: CopilotProposal<"signal">) => {
      const { data } = linkedProposal;
      setLinkedProposal(linkedProposal);
      setError(null);
      setForm((current) => mergeProposalIntoForm(current, data));
      setTestResultsState(
        data.evidence
          ? {
              status: "complete",
              data: data.evidence,
              completedAt: DateTime.fromISO(linkedProposal.created_at),
            }
          : { status: "idle" },
      );
    },
  );

  // One-shot link from an Ask Alpharun proposal card (?proposalId=...): the
  // proposal goes into the draft as soon as it loads. A proposal for another
  // signal loads nothing. While the link is unresolved, the form is held.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(SIGNAL_PROPOSAL_QUERY_PARAM),
  ).data;
  const {
    currentData: deepLinkProposal,
    isError: isDeepLinkProposalUnavailable,
  } = useGetPendingSignalProposalQuery(deepLinkProposalId ?? skipToken);
  const isLoadingProposal = deepLinkProposalId !== undefined;
  const clearDeepLinkProposalId = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(SIGNAL_PROPOSAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  useEffect(() => {
    if (deepLinkProposalId && isDeepLinkProposalUnavailable) {
      clearDeepLinkProposalId();
      setError("This suggestion is no longer available.");
    }
  }, [
    deepLinkProposalId,
    isDeepLinkProposalUnavailable,
    clearDeepLinkProposalId,
  ]);
  useEffect(() => {
    if (
      !isFormInitialized ||
      !deepLinkProposalId ||
      !deepLinkProposal ||
      deepLinkProposal.id !== deepLinkProposalId
    ) {
      return;
    }
    clearDeepLinkProposalId();
    const targetsThisPage =
      deepLinkProposal.data.kind === "create_signal"
        ? isNew
        : deepLinkProposal.resource_id === signalId;
    if (targetsThisPage) {
      putProposalIntoDraft(deepLinkProposal);
    }
  }, [
    isFormInitialized,
    deepLinkProposalId,
    deepLinkProposal,
    isNew,
    signalId,
    clearDeepLinkProposalId,
    putProposalIntoDraft,
  ]);

  const isSaving = isCreating || isSavingSignal;
  const isBusy = isLoadingProposal || isResolvingProposal || isSaving;
  useReportEditorProposal(linkedProposal?.id, isSaving);

  const { guardModal, navigateWithoutConfirmation } =
    useUnsavedChangesGuard(hasUnsavedChanges);

  if (!isNew && (isLoadingSignal || !isFormInitialized)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} isLoading />
      </div>
    );
  }

  if (!isNew && (isError || !signal)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} title="Signal not found" />
      </div>
    );
  }

  // Save writes the draft when it differs from the live signal, then applies
  // the suggestion in it. A clean draft with a suggestion still linked only
  // applies it.
  const handleSave = async () => {
    if (!isFormValid || isBusy) return;
    setError(null);
    const savedProposal = linkedProposal;
    if (hasUnsavedChanges) {
      try {
        const targetSignalId = signalId ?? createdSignalIdRef.current;
        if (targetSignalId) {
          const saved = await saveSignal({
            id: targetSignalId,
            ...draftPayload,
          }).unwrap();
          setForm(readFormFields(saved));
        } else {
          const created = await createSignal({
            ...draftPayload,
            ...(savedProposal?.data.kind === "create_signal"
              ? { proposal_id: savedProposal.id }
              : {}),
          }).unwrap();
          createdSignalIdRef.current = created.id;
          setCreatedSignalId(created.id);
          setForm(readFormFields(created));
          navigateWithoutConfirmation(() => {
            void navigate(
              appPaths.settingsSignal.build({ signalId: created.id }),
              { replace: true },
            );
          });
        }
      } catch (err: any) {
        console.error("Failed to save signal:", err);
        setError(
          err.data?.message || "Failed to save signal. Please try again.",
        );
        return;
      }
    }
    // The saved draft carried the suggestion, so it is applied. One that has
    // moved on (409) was already decided; the save stands either way. Any other
    // failure keeps the suggestion linked, so the next Save retries it.
    // TODO: Apply the proposal in the save's transaction on the server instead.
    if (savedProposal) {
      try {
        await resolveProposal({
          proposalId: savedProposal.id,
          status: "applied",
        }).unwrap();
      } catch (err: any) {
        if (err.status !== 409) {
          console.error("Failed to apply the suggestion:", err);
          setError(
            "The signal was saved, but the suggestion could not be marked as applied.",
          );
          return;
        }
      }
      setLinkedProposal((current) =>
        current === savedProposal ? null : current,
      );
    }
  };

  /** Drop the unsaved edits: back to the saved signal, or out of a new one. */
  const handleCancel = () => {
    if (isBusy) {
      return;
    }
    setError(null);
    setLinkedProposal(null);
    if (!signal) {
      navigateWithoutConfirmation(onClose);
      return;
    }
    setForm(readFormFields(signal));
  };

  const handleDelete = async () => {
    const targetSignalId = signalId ?? createdSignalIdRef.current;
    if (!targetSignalId || isDeleting) {
      return;
    }
    try {
      await deleteSignal(targetSignalId).unwrap();
      navigateWithoutConfirmation(onClose);
    } catch (err: any) {
      console.error("Failed to delete signal:", err);
      setError(
        err.data?.message || "Failed to delete signal. Please try again.",
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const testResultPillState =
    testResultsState.status === "idle" ? null : testResultsState;

  return (
    <>
      {guardModal}
      <SimpleModal
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        variant={SimpleModalVariant.Warning}
        title="Delete signal"
        subtitle="Are you sure you want to delete this signal? This action can't be undone."
        confirmButtonText="Delete signal"
        onConfirm={() => {
          void handleDelete();
        }}
      />
      <div className="flex h-full flex-col">
        <SignalEditorContainer
          name={form.name}
          description={form.description}
          metadataSchema={form.metadata_schema}
          playbookScope={form.playbook_scope}
          error={error}
          onNameChange={(name) => {
            updateForm({ name });
          }}
          onDescriptionChange={(description) => {
            updateForm({ description });
          }}
          onMetadataSchemaChange={(metadataSchema) => {
            updateForm({ metadata_schema: metadataSchema });
          }}
          onPlaybookScopeChange={(playbookScope) => {
            updateForm({ playbook_scope: playbookScope });
          }}
          diffBaseline={savedSignal ? readFormFields(savedSignal) : undefined}
          onCancel={handleCancel}
          onSave={() => {
            void handleSave();
          }}
          onDelete={
            signalId
              ? () => {
                  setIsDeleteModalOpen(true);
                }
              : undefined
          }
          isSaving={isSaving}
          isDeleting={isDeleting}
          isSaveDisabled={
            isBusy || !isFormValid || (!hasUnsavedChanges && !linkedProposal)
          }
          hasUnsavedChanges={hasUnsavedChanges || linkedProposal !== null}
          onTestSignalClick={handleTestSignalClick}
          onConfigureTestClick={handleConfigureTestClick}
          isTestSignalDisabled={
            !isFormValid || testResultsState.status === "loading"
          }
          testResultPillState={testResultPillState}
          onViewMatches={() => setIsMatchesModalOpen(true)}
        />
      </div>

      <SignalTestConfigModal
        isOpen={isTestConfigOpen}
        onClose={() => setIsTestConfigOpen(false)}
        initialFilters={effectiveTestFilters}
        onRunTest={handleRunTestFromModal}
      />

      {testResultsState.status === "complete" ? (
        <SignalTestMatchesModal
          isOpen={isMatchesModalOpen}
          onClose={() => setIsMatchesModalOpen(false)}
          data={testResultsState.data}
          metadataSchema={form.metadata_schema}
        />
      ) : null}
    </>
  );
};
